import { Bar, CartesianGrid, ComposedChart, Line, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { useDeck } from "../../lib/deck-context";
import { fmtMoney } from "../../lib/model";
import { Card, Eyebrow, Reveal } from "../deck/primitives";
import { STAGES, stageNumbers } from "./exitData";
import { Distribution } from "./Distribution";

const pct = (v) => `${(v * 100).toFixed(0)}%`;

const ChartTip = ({ active, payload, label }) =>
  active && payload?.length ? (
    <div className="rounded-lg bg-ink-deep text-white px-4 py-3 text-xs shadow-xl">
      <div className="font-medium mb-1">{label}</div>
      {payload.map((p) => (
        <div key={p.dataKey}>{p.name}: {p.dataKey === "roc" ? pct(p.value) : fmtMoney(p.value)}</div>
      ))}
    </div>
  ) : null;

const CompareChart = ({ rows }) => (
  <div data-testid="exit-compare-chart" className="h-[340px]">
    <ResponsiveContainer width="100%" height={340}>
      <ComposedChart data={rows} margin={{ top: 10, right: 10, left: 0, bottom: 0 }} barGap={4}>
        <CartesianGrid vertical={false} stroke="#EBEFF2" />
        <XAxis dataKey="name" tick={{ fontSize: 12, fill: "#7B8794" }} axisLine={{ stroke: "#D8E0E5" }} tickLine={false} />
        <YAxis yAxisId="l" tickFormatter={(v) => `$${(v / 1e6).toFixed(1)}M`} tick={{ fontSize: 11, fill: "#7B8794" }} axisLine={false} tickLine={false} width={48} />
        <YAxis yAxisId="r" orientation="right" tickFormatter={pct} tick={{ fontSize: 11, fill: "#2D6E6A" }} axisLine={false} tickLine={false} width={44} />
        <Tooltip content={<ChartTip />} cursor={{ fill: "rgba(45,110,106,0.06)" }} />
        <Bar yAxisId="l" dataKey="cash" name="Cash invested" fill="#C5D3D9" radius={[3, 3, 0, 0]} maxBarSize={44} />
        <Bar yAxisId="l" dataKey="profit" name="Profit" fill="#17324D" radius={[3, 3, 0, 0]} maxBarSize={44} />
        <Line yAxisId="r" dataKey="roc" name="Return on cash" stroke="#2D6E6A" strokeWidth={2.5} dot={{ r: 5, fill: "#2D6E6A" }} />
      </ComposedChart>
    </ResponsiveContainer>
  </div>
);

const Timeline = ({ rows }) => {
  const max = Math.max(...rows.map((r) => r.months), 1);
  return (
    <div data-testid="exit-timeline" className="space-y-4">
      {rows.map((r, i) => (
        <div key={r.key} className="grid grid-cols-[110px_1fr_56px] items-center gap-3">
          <span className="text-xs text-char whitespace-nowrap">{r.name}</span>
          <div className="h-2.5 rounded-full bg-paper overflow-hidden">
            <div className={`h-full rounded-full transition-[width] duration-500 ${i > 2 ? "bg-ink" : "bg-arq"}`} style={{ width: `${(r.months / max) * 100}%` }} />
          </div>
          <span data-testid={`exit-months-${r.key}`} className="text-right font-display text-lg text-ink tabular-nums">{r.months} mo</span>
        </div>
      ))}
    </div>
  );
};

export const ExitComparison = () => {
  const { m, inputs } = useDeck();
  const rows = STAGES.map((s) => ({ key: s.key, name: s.short, ...stageNumbers(s, m, inputs) })).map((r) => ({ ...r, profit: r.created }));
  return (
    <Reveal delay={0.1} className="mt-14" data-testid="exit-comparison">
      <Eyebrow>Exit Comparison</Eyebrow>
      <div className="mt-2 font-display text-3xl text-ink">Profit vs. cash invested vs. time</div>
      <div className="mt-6 grid xl:grid-cols-12 gap-6">
        <Card className="xl:col-span-7 p-7">
          <div className="flex flex-wrap gap-5 text-[11px] text-steel mb-5">
            <span className="flex items-center gap-2"><span className="h-2.5 w-4 rounded-sm bg-[#C5D3D9]" />Cash invested</span>
            <span className="flex items-center gap-2"><span className="h-2.5 w-4 rounded-sm bg-ink" />Profit</span>
            <span className="flex items-center gap-2"><span className="h-[3px] w-4 rounded bg-arq" />Return on cash</span>
          </div>
          <CompareChart rows={rows} />
        </Card>
        <Card className="xl:col-span-5 p-7">
          <Eyebrow>Estimated Months From Today</Eyebrow>
          <div className="mt-6"><Timeline rows={rows} /></div>
        </Card>
      </div>
      <Card className="mt-6 overflow-x-auto">
        <table data-testid="exit-compare-table" className="data-table w-full min-w-[900px]">
          <thead>
            <tr><th>Exit</th><th className="!text-right">Months</th><th className="!text-right">Cash Invested</th><th className="!text-right">Profit</th><th className="!text-right">Return on Cash</th><th className="!text-right">Annualized*</th><th className="!text-right !text-arq">Arqitek Profit</th><th className="!text-right">Partner Profit</th></tr>
          </thead>
          <tbody>
            {rows.map((r, i) => (
              <tr key={r.key} data-testid={`exit-compare-row-${r.key}`}>
                <td className="text-ink"><span className="font-mono text-[10px] text-arq mr-3">EXIT {i + 1}</span>{r.name}</td>
                <td className="text-right tabular-nums">{r.months}</td>
                <td className="text-right tabular-nums">{fmtMoney(r.cash)}</td>
                <td className="text-right tabular-nums text-ink font-medium">{fmtMoney(r.profit)}</td>
                <td className="text-right tabular-nums">{pct(r.roc)}</td>
                <td className="text-right tabular-nums text-steel">{pct(r.annual)}</td>
                <td data-testid={`exit-arq-share-${r.key}`} className="text-right tabular-nums text-arq font-medium">{fmtMoney(r.arq)}</td>
                <td data-testid={`exit-partner-share-${r.key}`} className="text-right tabular-nums">{fmtMoney(r.partner)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>
      <Distribution rows={rows} />
      <p className="mt-3 text-xs text-steel leading-relaxed">
        Cash invested = cumulative predevelopment spend for Exits 1–3; working equity ({fmtMoney(m.equity)}) for Exits 4–5, with construction debt funding the balance. Arqitek / Partner profit follows the capital-first waterfall below — return of invested capital is in addition. *Simple annualized return (return on cash ÷ years), not IRR. Months are planning estimates and editable in Assumptions.
      </p>
    </Reveal>
  );
};
